import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

type Period = '7d' | '30d' | '90d';
const DAYS_MAP: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90 };

interface SnapshotRow {
  date: string;
  roas: number;
  revenue: number;
  spend: number;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ payload?: SnapshotRow }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm shadow-lg">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      <p className="text-blue-400">ROAS: {d?.roas?.toFixed(2)}x</p>
      <p className="text-emerald-400">Revenue: ${d?.revenue?.toLocaleString()}</p>
      <p className="text-rose-400">Spend: ${d?.spend?.toLocaleString()}</p>
    </div>
  );
}

function formatSnapshotDate(raw: string | Date): string {
  const s = typeof raw === 'string' ? raw : raw.toISOString();
  // "2026-08-13" → "Aug 13"
  const d = new Date(s + (s.length === 10 ? 'T00:00:00Z' : ''));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function ROASTrend() {
  const [period, setPeriod] = useState<Period>('30d');
  const [data, setData] = useState<SnapshotRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    setLoading(true);
    trpc.roas.trends.query({ tenantId: user.tenantId, days: DAYS_MAP[period] })
      .then(snapshots => {
        setData(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (snapshots as any[]).map(s => ({
            date: formatSnapshotDate(s.date),
            roas: parseFloat(s.roas ?? '0'),
            revenue: parseFloat(s.revenue ?? '0'),
            spend: parseFloat(s.cost ?? '0'),
          })),
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">ROAS Trend</h3>
        <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
          {(['7d', '30d', '90d'] as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                period === p
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-[220px] flex items-center justify-center">
          <div className="text-slate-600 text-sm animate-pulse">Loading…</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center flex-col gap-2">
          <p className="text-slate-600 text-sm">No trend data available yet.</p>
          <p className="text-slate-700 text-xs">Data will appear as ROAS snapshots are recorded.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="roasGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              interval={period === '90d' ? 14 : period === '30d' ? 6 : 0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              domain={['auto', 'auto']}
              tickFormatter={(v: number) => `${v.toFixed(1)}x`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="roas"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#roasGrad)"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
